// useOptions — the ONE hook behind every picker (ADR-080 §3 · §7): a list comes
// from one of three places, decided once per KIND and named in a SOURCE
// descriptor beside its entity's hook — the CODE (a vocabulary, built once by
// the caller), a SMALL list (asked WHOLE when its picker opens, a minute fresh,
// invalidated by its own mutations; it shares the entity's own list query key
// only when that list is whole — zones · clusters · roles), or the SERVER (the
// bounded lookup, `useLookup`: 20 by the typed search, the rows of
// given ids). A screen names the kind; the hook is ONE shape for the three —
// one query whose key and fetch follow the kind, one batch of name questions for
// the server kind, one memoised browser match for the other two (`createMatcher`
// — the same word rules as the server's builder, ADR-072 §4). Nothing loads
// while the picker is closed (`open: false`), except the NAMES of what is
// already chosen (a value in the link at page open). Rows carry `pickable`
// (false = greyed, the rule refuses it), `inactive` (a small list's disabled
// value — always shown when chosen; otherwise CHOOSE ≠ FILTER BY, ADR-080 §5: a
// filter hides it at rest and offers it marked after the live ones once typed or
// behind "Show disabled (n)"; a FIELD never offers it, typed or not — the server
// refuses a new use) and the suggestion GROUP the server put them in. A kinded
// value (`site:<id>` — ADR-037 §2) is the source's `valueKind`: the hook
// prefixes what it answers and strips what it is given, so a filter that mixes
// kinds later composes kinded sources without touching a screen. A filter's
// people carry the viewer as the token `me` (ADR-080 §6): the source names it,
// nothing asks the server for it.

import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createMatcher } from '@ems-app/schemas/common/search-rules';
import { useLookup, type LookupEntity, type LookupRowOf, type LookupSource } from './useLookup.js';

/** A short list holds this many rows or fewer — no search box, its own order kept (ADR-080 §11). */
export const SHORT_LIST = 8;
/** The token a filter's link and wire carry for the viewer (ADR-080 §6). */
export const ME = 'me';

export interface OptionRow {
  /** The value a field stores (a kinded source: `site:<id>`; a filter's viewer: `me`). */
  id: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  trailing?: ReactNode;
  /** A badge's word (a status · "Will be granted scope"): READ with the row after its second line, never DRAWN on it —
   *  the badge beside the row is the eye's copy and is hidden from a screen reader, so the word is said once each way. */
  spoken?: string;
  /** What the field shows once chosen, when it differs from the row's first line. */
  selectedLabel?: string;
  /** What a filter's chip says on HOVER for this chosen value, when more than its label ("Last 7 days · since Sat 4 Oct"). */
  hover?: string;
  /** False = GREYED — the rule refuses it (a paused thing); its reason is on its second line. */
  pickable: boolean;
  /** A small list's disabled value: hidden at rest, offered marked after the live ones once typed; shown when chosen. */
  inactive?: boolean;
  /** The server's suggestion group (ADR-080 §6) — null for an ordinary row. */
  group: string | null;
  /** The parent's value (a dependent small list — a cluster carries its zone). */
  parent?: string | null;
  /** A group row's children — two levels in place (a zone › its clusters); a box picks the whole group, an arrow opens it. */
  children?: OptionRow[];
  /** A group row held OPEN — no arrow (its kind is reached by typing; the rows under it are a few that lead — "All sites"
   *  over the followed). */
  fixedOpen?: boolean;
  /** Covered by a whole picked above it (a typed depot while "All depots" is ticked): drawn ticked, greyed, "Included",
   *  never picked on its own (ADR-080 §8). */
  included?: boolean;
  /** Every child of a group when the list shows only some of them (a tree narrowed by the typed text): the highest
   *  pick is pruned against all of them, never the shown few (ADR-080 §8). */
  childIds?: readonly string[];
  /** A DEEP tree's link (the administrative areas): the id of the row this one sits directly inside, `null` at the
   *  top. The Navigator walks it one level at a time (ADR-080 §14) — the matcher still answers across every level;
   *  `parent` stays the dependency mark (an area's country). */
  inside?: string | null;
  /** A deep tree's row: the names above it, top first ("Mount Lebanon / Aley") — under a search match and under the
   *  chosen value's name; never matched (a `description` is, as a code). */
  path?: string;
}

/** What a server row's FACE answers (`lookupFaces` — one face per entity, read into the option model). */
export interface FacedRow {
  value: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  trailing?: ReactNode;
  /** The badge's word, read with the row and never drawn twice (`OptionRow.spoken`). */
  spoken?: string;
  selectedLabel?: string;
  disabled?: boolean;
}

interface CodeSourceBase {
  kind: 'code';
  /** Names the vocabulary — with `rows`, the memo key of its matcher (a descriptor may be built inline). */
  key: string;
  rows: readonly OptionRow[];
  valueKind?: string;
  /** The vocabulary is still being READ (rows a screen builds from its own read — the site form's countries, a files tab's lists): nothing
   *  is settled, no chosen value is unavailable, an open list says "Loading…". */
  pending?: boolean;
}
/** A vocabulary whose rows stand on their own. */
export interface CodeSource extends CodeSourceBase {
  parented?: false;
}
/** A vocabulary whose rows carry a PARENT (a battery technology its chemistry): a field on it must declare
 *  `dependsOn` (ADR-080 §9) — the `Picker`'s type refuses it otherwise. */
export interface ParentedCodeSource extends CodeSourceBase {
  parented: true;
}

interface SmallSourceBase<D> {
  kind: 'small';
  /** The entity's own list key when that list is whole (the admin page and the picker share one cache entry), else the picker's own. */
  queryKey: readonly unknown[];
  // Method syntax on purpose: a descriptor typed on its own answer must still be an `OptionSource`
  // (a method's parameter is checked bivariantly; a property's function type is not).
  /** The read — the cache holds its raw answer (shared with the entity's own hook), `rows` projects it per picker. */
  fetch(signal: AbortSignal): Promise<D>;
  /** The whole read as rows — every row with its `inactive` mark; a dependent list with its `parent`. A PURE
   *  projection of the data: the hook memoises it by the query key, so a descriptor may be built inline. */
  rows(data: D): OptionRow[];
  valueKind?: string;
}
/** A small list whose rows stand on their own. */
export interface SmallSource<D = unknown> extends SmallSourceBase<D> {
  parented?: false;
}
/** A small list whose rows carry a PARENT (a cluster its zone): a field on it must declare `dependsOn`
 *  (ADR-080 §9) — the `Picker`'s type refuses it otherwise. */
export interface ParentedSource<D = unknown> extends SmallSourceBase<D> {
  parented: true;
}

interface ServerSourceBase<E extends LookupEntity> {
  kind: 'server';
  projectId: string;
  lookup: LookupSource<E>;
  face: (row: LookupRowOf<E>) => FacedRow;
  valueKind?: string;
  /** A filter's people: the viewer's own row answers as the token `me`, named here (ADR-080 §6). */
  me?: { id: string; label: string };
  /** Rows the host already holds (an edit form's product): met at mount, naming what is chosen before any read — the one
   *  way a kind whose read names no ids (the products' search) names its chosen. */
  known?: readonly LookupRowOf<E>[];
}
/** A bounded lookup whose rule stands on its own (a site · a person). */
export interface ServerSource<E extends LookupEntity = LookupEntity> extends ServerSourceBase<E> {
  parented?: false;
}
/** A bounded lookup whose rule stands INSIDE a container (the units at a site): a field on it must declare
 *  `dependsOn` — on a parent change its picks clear deterministically, the server re-checks on Save (ADR-080 §9). */
export interface ParentedServerSource<E extends LookupEntity = LookupEntity> extends ServerSourceBase<E> {
  parented: true;
}

/** One KIND of a mixed source (ADR-080 §8 — a filter that mixes kinds; its values carry the kind, `site:<id>`, and the
 *  whole kind is `site:*`): the kind row that picks it whole, and where its places come from. */
export interface MixedKind {
  /** The token's kind (`site` · `depot` · `vehicle`). */
  kind: string;
  /** The kind row — "All sites" — and the glyph every row of the kind wears. */
  label: string;
  icon: ReactNode;
  /** The kind's section once something is typed ("Sites"). */
  heading: string;
  /** The kind's places — a source standing on its own, WITHOUT a `valueKind` (the mixed source tags). A place is named
   *  by the viewer's reach, never by a key (the naming rule): every offered kind has its places; a kind the project
   *  does not have (the vehicles with the module off) is left out of the source altogether. */
  places: SmallSource | ServerSource;
  /** At rest: `search` — the places are found by typing only (thousands: the kind row never opens; the kind's suggestion
   *  group — the followed — leads under it, held open) · `open` — the kind row opens in place (a small list whole, or
   *  the server's first rows and "keep typing"). */
  rest: 'search' | 'open';
  /** How the kind's suggestion group shows under its row at rest (the followed sites: the star, "Followed" read aloud). */
  suggested?: { icon: ReactNode; word: string };
}
/** A filter that MIXES kinds (the Location filter — sites · depots · vehicles): each kind's source composed, at most
 *  three, two levels at most (a kind › its places — ADR-080 §14). Picks add up across kinds; a kind picked whole stores
 *  `<kind>:*` alone. A filter's source only (`useMixedOptions`). */
export interface MixedSource {
  kind: 'mixed';
  key: string;
  kinds: readonly [MixedKind] | readonly [MixedKind, MixedKind] | readonly [MixedKind, MixedKind, MixedKind];
  /** The search box's words ("Search sites, depots, vehicles"). */
  placeholder: string;
}

/** The sources a field may not take without `dependsOn`. */
export type DependentSource = ParentedCodeSource | ParentedSource | ParentedServerSource;
export type OptionSource = CodeSource | ParentedCodeSource | SmallSource | ParentedSource | ServerSource | ParentedServerSource;
export type SourceKind = OptionSource['kind'];

/** A vocabulary as a source (the code kind) — built once by the caller (a memo on its words). */
export function codeSource(key: string, rows: readonly OptionRow[], valueKind?: string): CodeSource {
  return { kind: 'code', key, rows, valueKind };
}
/** A fixed vocabulary as a source — its values in their order, each worded by `label` (a list page's statuses, grades,
 *  modes) — built once by the caller (a memo on its words). */
export function vocabularySource<V extends string>(key: string, values: readonly V[], label: (value: V) => string, valueKind?: string): CodeSource {
  return codeSource(key, values.map((v) => ({ id: v, label: label(v), pickable: true, group: null })), valueKind);
}
/** A vocabulary whose rows carry their `parent` — a field on it declares `dependsOn` (the type insists). */
export function parentedCodeSource(key: string, rows: readonly OptionRow[], valueKind?: string): ParentedCodeSource {
  return { kind: 'code', key, rows, valueKind, parented: true };
}
/** A list of value · label pairs as a code source — a fixture a story holds, a page's own short list. */
export function pairsSource(key: string, pairs: readonly { value: string; label: string }[]): CodeSource {
  return codeSource(key, pairs.map((p) => ({ id: p.value, label: p.label, pickable: true, group: null })));
}

export interface UseOptionsArgs {
  /** The picker is open — the list loads on the press, never before (ADR-080 §7). */
  open: boolean;
  /** The typed text (the search box's). */
  text: string;
  /** The chosen values, as the field stores them. */
  chosen: readonly string[];
  /** A dependent small list's parent value(s): only their rows are offered (`null` / none = no parent chosen — every row). */
  parent?: string | readonly string[] | null;
  /** False = nothing is asked at all (a host that pre-mounts a closed drawer). */
  enabled?: boolean;
  /** A short list's "Show disabled (n)": the inactive rows are offered at rest too, after the live ones, marked. */
  showInactive?: boolean;
  /** Who asks (ADR-080 §5 — choose ≠ filter by): a `filter` (the default) reaches the inactive rows once typed or shown;
   *  a `field` offers what can be picked NOW — an inactive row only when it is the chosen one, marked. */
  purpose?: 'field' | 'filter';
}

export interface Options {
  /** The kind behind the answer — `mixed` for a filter that mixes kinds (`useMixedOptions`). */
  kind: SourceKind | 'mixed';
  /** The rows for the text as it stands — the server's order (its groups first), or the browser match. */
  rows: readonly OptionRow[];
  /** The WHOLE list for the browser kinds, whatever is typed (a deep tree's host walks it — a match's ancestry, the
   *  highest pick); null for the server kind and until the list has landed. */
  whole: readonly OptionRow[] | null;
  /** The chosen values, named as far as they are known, in the chosen order. */
  chosenRows: readonly OptionRow[];
  /** A chosen value the source answered ABSENT to (outside the viewer's scope, gone, of another kind): its chip says "Not available". */
  unavailable: ReadonlySet<string>;
  /** The whole list's size for the browser kinds (the search box past SHORT_LIST — judged on the FULL list, never a narrowed one); null for the server. */
  total: number | null;
  /** The list is searched — a search box shows (a server kind always; a browser kind past SHORT_LIST). */
  searchable: boolean;
  /** The server's cap cut the answer ("More — keep typing"). */
  hasMore: boolean;
  /** The start list is in flight with nothing to show yet. */
  loading: boolean;
  /** Something is typed and its answer is not settled (the server kind). */
  searching: boolean;
  /** Some chosen values are still being named. */
  naming: boolean;
  failed: boolean;
  /** The start answer has arrived (or failed) — a screen that seeds a form from it waits for this. */
  settled: boolean;
  /** The inactive rows this list HIDES at rest — under the parent; a chosen one is already shown (a short list offers "Show disabled (n)"). */
  inactiveCount: number;
}

const NO_LOOKUP: LookupSource<'sites'> = { entity: 'sites', params: {} };
const EMPTY: readonly string[] = [];

const tagWith = (kind: string | undefined) => (id: string): string => (kind ? `${kind}:${id}` : id);
/** The id behind a value of this source's kind — null for a value of another kind. */
const untagWith = (kind: string | undefined) => (value: string): string | null => {
  if (!kind) return value;
  const head = `${kind}:`;
  return value.startsWith(head) ? value.slice(head.length) : null;
};

/** A faced server row as the family's row — for a host that lists lookup rows outside the hook (the files' "What does
 *  it show?" lists a kind's units through `useLookup` itself); `group` is the server's, or a host's own heading. */
export function optionRowOf(faced: FacedRow, group: string | null): OptionRow {
  return fromFace(faced, group, (id) => id);
}

function fromFace(faced: FacedRow, group: string | null, tag: (id: string) => string): OptionRow {
  return {
    id: tag(faced.value),
    label: faced.label,
    description: faced.description,
    spoken: faced.spoken,
    icon: faced.icon,
    trailing: faced.trailing,
    selectedLabel: faced.selectedLabel,
    pickable: !faced.disabled,
    group,
  };
}

/** Typed, a group's matches stay TOGETHER — the lead groups first, in the order the list names them, the rest last
 *  (ADR-080 §6: "a group's matches first, nothing lifted"): a ranked match never interleaves a heading's rows with
 *  the rest (the list draws one section per run it is given). Stable: inside a group the match order stands. */
function groupsFirst(rows: readonly OptionRow[], all: readonly OptionRow[]): readonly OptionRow[] {
  const order = new Map<string, number>();
  for (const r of all) if (r.group !== null && !order.has(r.group)) order.set(r.group, order.size);
  if (order.size === 0) return rows;
  const rank = (r: OptionRow) => (r.group === null ? order.size : (order.get(r.group) ?? order.size));
  return [...rows].sort((a, b) => rank(a) - rank(b));
}

/** A tree once typed (a zone › its clusters): a group that matched stands whole; one whose children matched narrows to
 *  them, held open; a lone row stands as it is — a matched child is never listed beside its group. */
function narrowTree(all: readonly OptionRow[], found: readonly OptionRow[]): readonly OptionRow[] {
  const hit = new Set(found.map((r) => r.id));
  return all.flatMap((r) => {
    const children = r.children ?? [];
    if (children.length === 0) return hit.has(r.id) ? [r] : [];
    if (hit.has(r.id)) return [r];
    const kept = children.filter((c) => hit.has(c.id));
    return kept.length > 0 ? [{ ...r, children: kept, fixedOpen: true, childIds: children.map((c) => c.id) }] : [];
  });
}

/** The viewer's own row of a filter's people answers as the token (its name on the second line). */
function asMe(row: OptionRow, me: NonNullable<ServerSource['me']>): OptionRow {
  return { ...row, id: ME, label: me.label, description: [row.label, row.description].filter(Boolean).join(' · ') || undefined, selectedLabel: me.label };
}

export function useOptions(source: OptionSource, args: UseOptionsArgs): Options {
  const { open, text, chosen: chosenRaw, parent = null, enabled = true, showInactive = false, purpose = 'filter' } = args;
  const field = purpose === 'field';
  // A link is read tolerantly (ADR-037): `?x=a,a` names one row, never two.
  const chosen = useMemo(() => (new Set(chosenRaw).size === chosenRaw.length ? chosenRaw : [...new Set(chosenRaw)]), [chosenRaw]);
  const server = source.kind === 'server' ? source : null;
  const small = source.kind === 'small' ? source : null;
  const tag = tagWith(source.valueKind);
  const untag = untagWith(source.valueKind);

  // The chosen values the server is asked to name: this kind's ids, never the token.
  const chosenIds = useMemo(
    () => (server ? chosen.filter((v) => v !== ME).map(untag).filter((id): id is string => id !== null) : EMPTY),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- untag follows source.valueKind
    [server, chosen, source.valueKind],
  );

  // ── the server kind: the bounded lookup ──
  const look = useLookup(server?.projectId ?? '', (server?.lookup ?? NO_LOOKUP) as LookupSource, chosenIds, { enabled: enabled && server !== null, open, text, known: server?.known as readonly LookupRowOf<LookupEntity>[] | undefined });

  // ── the small kind: the WHOLE read, on the press (or for the names of a value already chosen) ──
  const smallQuery = useQuery({
    queryKey: small?.queryKey ?? ['options', 'none'],
    queryFn: ({ signal }) => small!.fetch(signal),
    enabled: enabled && small !== null && (open || chosen.length > 0),
    staleTime: 60_000,
  });
  // Memoised by the QUERY KEY, never the descriptor's identity: `rows` is a pure projection (the contract above),
  // and a descriptor built inline (`zonesSource(projectId)` in a render) must not rebuild the rows — and with them
  // the matcher — on every render.
  const smallKey = small ? JSON.stringify(small.queryKey) : null;
  const smallRows = useMemo(
    () => (small && smallQuery.data !== undefined ? small.rows(smallQuery.data) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `small` stands in by its query key (a pure projection)
    [smallKey, smallQuery.data],
  );

  // ── the browser kinds: every row (tagged), ONE matcher per data set (a code source by its key + rows) ──
  const codeRows = source.kind === 'code' ? source.rows : null;
  const codeKey = source.kind === 'code' ? source.key : null;
  const codePending = source.kind === 'code' && source.pending === true;
  const allRows = useMemo<readonly OptionRow[] | null>(() => {
    if (codeRows) return source.valueKind ? codeRows.map((r) => ({ ...r, id: tag(r.id) })) : codeRows;
    if (smallRows) return source.valueKind ? smallRows.map((r) => ({ ...r, id: tag(r.id) })) : smallRows;
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tag follows source.valueKind
  }, [codeKey, codeRows, smallRows, source.valueKind]);
  // A group row's children are rows too (a zone › its clusters): matched by the typed text and named when chosen.
  const flatRows = useMemo(() => (allRows ? allRows.flatMap((r) => [r, ...(r.children ?? [])]) : null), [allRows]);
  const matcher = useMemo(() => (flatRows ? createMatcher(flatRows, (r) => ({ name: r.label, codes: [r.description] })) : null), [flatRows]);

  // The lookup's answer by its fields: the hook's object is new each render, its fields are not.
  const { rows: lookRows, known: lookKnown, unavailable: lookUnavailable, hasMore: lookHasMore, settled: lookSettled, typing: lookTyping, searching: lookSearching, naming: lookNaming, failed: lookFailed } = look;
  const isServer = server !== null;
  const face = server?.face;
  const meId = server?.me?.id;
  const meLabel = server?.me?.label;

  return useMemo<Options>(() => {
    if (isServer && face) {
      const me = meId !== undefined && meLabel !== undefined ? { id: meId, label: meLabel } : undefined;
      // The viewer's own row answers as the token — unless the chosen already holds the viewer's RAW id (a link
      // another person built by picking them): then the row keeps that id, so it is found, ticked and named.
      const rowOf = (r: LookupRowOf<LookupEntity>): OptionRow => {
        const row = fromFace(face(r as never), r.group, tag);
        if (!me || r.id !== me.id) return row;
        const asRow = asMe(row, me);
        return chosen.includes(tag(r.id)) ? { ...asRow, id: tag(r.id) } : asRow;
      };
      const rows = lookRows.map(rowOf);
      const known = new Map<string, OptionRow>();
      for (const r of lookKnown.values()) {
        const row = rowOf(r);
        known.set(row.id, row);
        if (me && r.id === me.id) known.set(tag(r.id), row);
      }
      const unavailable = new Set<string>();
      const chosenRows: OptionRow[] = [];
      for (const v of chosen) {
        if (v === ME) {
          chosenRows.push(me ? { id: ME, label: me.label, pickable: true, group: null } : { id: ME, label: ME, pickable: true, group: null });
          continue;
        }
        const id = untag(v);
        if (id === null) {
          unavailable.add(v);
          continue;
        }
        const row = known.get(v);
        if (row) chosenRows.push(row);
        else if (lookUnavailable.has(id)) unavailable.add(v);
      }
      return {
        kind: 'server',
        rows,
        whole: null,
        chosenRows,
        unavailable,
        total: null,
        searchable: true,
        hasMore: lookHasMore,
        loading: open && !lookSettled && lookRows.length === 0 && !lookTyping,
        searching: lookSearching,
        naming: lookNaming,
        failed: lookFailed,
        settled: lookSettled,
        inactiveCount: 0,
      };
    }
    // The browser kinds.
    const typed = text.trim().length > 0;
    const foundAll = matcher && allRows ? matcher.match(text, { order: allRows.length <= SHORT_LIST ? 'kept' : 'ranked' }) : [];
    // A tree stays a tree: at rest the groups whole, their children in place; typed, narrowed by group (`narrowTree`).
    const tree = allRows?.some((r) => (r.children?.length ?? 0) > 0) ?? false;
    const found = !tree || !allRows ? foundAll : typed ? narrowTree(allRows, foundAll) : allRows;
    const matched = typed && allRows ? groupsFirst(found, allRows) : found;
    const parents = parent === null ? null : typeof parent === 'string' ? [parent] : parent.length === 0 ? null : parent;
    const underParent = parents === null ? matched : matched.filter((r) => r.parent === undefined || r.parent === null || parents.includes(r.parent));
    // At rest the live rows — and a CHOSEN inactive row in its place, marked (ADR-080 §5: hidden at rest, shown once
    // typed or chosen); for a filter, typed — or when "Show disabled" is on — the other inactive rows follow the live,
    // marked; a field never offers them (choose ≠ filter by). A chosen one keeps its place.
    const chosenSet = new Set(chosen);
    const offered = !field && (typed || showInactive);
    const hidden = (r: OptionRow) => r.inactive === true && !chosenSet.has(r.id);
    // Inside a group the same rule: a hidden inactive child is dropped (a chosen one stays, marked); offered, the
    // inactive children follow the live ones. A group that is itself inactive stays in place for its surviving
    // children — shown, not pickable; offered, it follows the live rows like any inactive row.
    const inGroup = (r: OptionRow): OptionRow => {
      if (!r.children || r.children.length === 0) return r;
      const live = r.children.filter((c) => !hidden(c));
      const rest = offered ? r.children.filter(hidden) : [];
      const children = [...live, ...rest];
      return children.length === r.children.length ? r : { ...r, children };
    };
    const kept = underParent.map(inGroup);
    const inPlace = kept.filter((r) => !hidden(r) || (!offered && (r.children?.length ?? 0) > 0)).map((r) => (hidden(r) ? { ...r, pickable: false } : r));
    const rows = offered ? [...inPlace, ...kept.filter(hidden)] : inPlace;
    const byId = new Map((flatRows ?? []).map((r) => [r.id, r] as const));
    const chosenRows = chosen.map((v) => byId.get(v)).filter((r): r is OptionRow => r !== undefined);
    const settled = source.kind === 'code' ? !codePending : allRows !== null || smallQuery.isError;
    const unavailable = new Set(settled && allRows ? chosen.filter((v) => !byId.has(v)) : []);
    return {
      kind: source.kind,
      rows,
      whole: allRows,
      chosenRows,
      unavailable,
      total: allRows?.length ?? null,
      searchable: (allRows?.length ?? 0) > SHORT_LIST,
      hasMore: false,
      loading: (small !== null && open && allRows === null && smallQuery.isPending) || (codePending && open),
      searching: false,
      naming: small !== null && !open && chosen.length > 0 && allRows === null && smallQuery.isPending,
      failed: smallQuery.isError,
      settled,
      // The inactive rows this list HIDES — under the parent, as the rows are; a chosen one is already shown (a short
      // list offers "Show disabled (n)"). A field offers none of them, so it counts none.
      inactiveCount: field ? 0 : underParent.filter(hidden).length + underParent.flatMap((r) => r.children ?? []).filter(hidden).length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tag/untag follow source.valueKind; `server` / `small` stand in by their fields (face · me · the query key)
  }, [isServer, face, meId, meLabel, smallKey, source.kind, source.valueKind, codePending, lookRows, lookKnown, lookUnavailable, lookHasMore, lookSettled, lookTyping, lookSearching, lookNaming, lookFailed, allRows, flatRows, matcher, text, parent, chosen, open, showInactive, field, smallQuery.isError, smallQuery.isPending]);
}
